import React from 'react'
import { useForm } from 'react-hook-form'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'

import { updateConfig } from '../api/config'
import LoadingSpinner from '../components/LoadingSpinner'

const SettingsConfig = ({ config }) => {
  const queryClient = useQueryClient()

  const updateConfigMutation = useMutation({
    mutationFn: (data) => updateConfig('settings', data),
    onSuccess: () => {
      queryClient.invalidateQueries(['config'])
      toast.success('设置已应用')
    },
    onError: (error) => {
      toast.error(`应用失败: ${error.message}`)
    },
  })

  // 确保config至少是一个空对象
  const settingsConfig = config || {}

  // 通用的设置卡片组件
  const SettingsCard = ({ title, children, defaultValues, sectionKey }) => {
    const { register, handleSubmit, watch, formState: { isDirty }, reset } = useForm({
      defaultValues
    })

    const watchedValues = watch()

    const handleFormSubmit = (data) => {
      let updatedConfig
      let processedData = { ...data }

      // 特殊处理 providers 字段 - 将JSON字符串转换为对象
      if (processedData.providers && typeof processedData.providers === 'string') {
        try {
          processedData.providers = JSON.parse(processedData.providers)
        } catch (error) {
          toast.error('API提供商配置JSON格式错误')
          return
        }
      }

      if (sectionKey) {
        updatedConfig = { ...settingsConfig, [sectionKey]: { ...settingsConfig[sectionKey], ...processedData } }
      } else {
        updatedConfig = { ...settingsConfig, ...processedData }
      }

      updateConfigMutation.mutate(updatedConfig, {
        onSuccess: () => {
          // 成功后重置表单状态，使用新的默认值
          const newDefaultValues = sectionKey
            ? Object.keys(defaultValues).reduce((acc, key) => {
              acc[key] = processedData[key]
              return acc
            }, {})
            : processedData
          reset(newDefaultValues)
        }
      })
    }

    return (
      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">{title}</h3>
          </div>
          <div className="card-content">
            {children({ register, watch, watchedValues })}
            <div className="flex justify-end mt-4">
              <button
                type="submit"
                disabled={!isDirty || updateConfigMutation.isLoading}
                className={`btn-primary ${!isDirty ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {updateConfigMutation.isLoading ? (
                  <>
                    <LoadingSpinner size="sm" className="mr-2" />
                    应用中...
                  </>
                ) : (
                  '应用设置'
                )}
              </button>
            </div>
          </div>
        </div>
      </form>
    )
  }

  // 如果config是undefined，显示加载状态
  // 如果config是空对象，使用默认值
  if (config === undefined) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* 基本信息 */}
      <SettingsCard
        title="基本信息"
        defaultValues={{
          title: settingsConfig?.title || '',
          description: settingsConfig?.description || '',
          language: settingsConfig?.language || 'zh-CN',
          favicon: settingsConfig?.favicon || '',
          startUrl: settingsConfig?.startUrl || '',
          base: settingsConfig?.base || '',
          instanceName: settingsConfig?.instanceName || ''
        }}
      >
        {({ register, watchedValues }) => (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="form-group">
              <label className="form-label">页面标题</label>
              <input
                type="text"
                className="input"
                {...register('title')}
                placeholder="我的导航页"
              />
              <p className="form-help">自定义页面的标题</p>
            </div>

            <div className="form-group">
              <label className="form-label">页面描述</label>
              <input
                type="text"
                className="input"
                {...register('description')}
                placeholder="我的个人导航页面"
              />
              <p className="form-help">自定义页面的描述</p>
            </div>

            <div className="form-group">
              <label className="form-label">语言</label>
              <select className="select" {...register('language')}>
                <option value="ca">Català</option>
                <option value="de">Deutsch</option>
                <option value="en">English</option>
                <option value="es">Español</option>
                <option value="fr">Français</option>
                <option value="he">עברית</option>
                <option value="hr">Hrvatski</option>
                <option value="hu">Magyar</option>
                <option value="it">Italiano</option>
                <option value="ja">日本語</option>
                <option value="ko">한국어</option>
                <option value="nb-NO">Norsk (bokmål)</option>
                <option value="nl">Nederlands</option>
                <option value="pt">Português</option>
                <option value="ru">Русский</option>
                <option value="sv">Svenska</option>
                <option value="vi">Tiếng Việt</option>
                <option value="zh-CN">中文 (简体)</option>
                <option value="zh-Hant">中文 (繁體)</option>
              </select>
              <p className="form-help">选择界面显示语言</p>
            </div>

            <div className="form-group">
              <label className="form-label">网站图标 URL</label>
              <input
                type="text"
                className="input"
                {...register('favicon')}
                placeholder="https://example.com/favicon.ico"
              />
              <p className="form-help">自定义网站图标，支持完整URL或相对路径</p>
            </div>

            <div className="form-group">
              <label className="form-label">起始 URL</label>
              <input
                type="text"
                className="input"
                {...register('startUrl')}
                placeholder="https://custom.url"
              />
              <p className="form-help">PWA应用的起始URL，默认为"/"</p>
            </div>

            <div className="form-group">
              <label className="form-label">基础 URL</label>
              <input
                type="text"
                className="input"
                {...register('base')}
                placeholder="http://host.local/homepage"
              />
              <p className="form-help">某些代理配置中需要设置的基础URL</p>
            </div>

            <div className="form-group">
              <label className="form-label">实例名称</label>
              <input
                type="text"
                className="input"
                {...register('instanceName')}
                placeholder="public"
              />
              <p className="form-help">用于区分多个Homepage实例</p>
            </div>
          </div>
        )}
      </SettingsCard>

      {/* 外观设置 */}
      <SettingsCard
        title="外观设置"
        defaultValues={{
          theme: settingsConfig?.theme || 'dark',
          color: settingsConfig?.color || 'slate',
          iconStyle: settingsConfig?.iconStyle || 'gradient',
          cardBlur: settingsConfig?.cardBlur || '',
          bookmarksStyle: settingsConfig?.bookmarksStyle || ''
        }}
      >
        {({ register, watchedValues }) => (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="form-group">
              <label className="form-label">主题</label>
              <select className="select" {...register('theme')}>
                <option value="light">浅色</option>
                <option value="dark">深色</option>
              </select>
              <p className="form-help">选择浅色或深色主题</p>
            </div>

            <div className="form-group">
              <label className="form-label">主题色</label>
              <select className="select" {...register('color')}>
                <option value="slate">石板灰</option>
                <option value="gray">灰色</option>
                <option value="zinc">锌色</option>
                <option value="neutral">中性色</option>
                <option value="stone">石色</option>
                <option value="red">红色</option>
                <option value="orange">橙色</option>
                <option value="amber">琥珀色</option>
                <option value="yellow">黄色</option>
                <option value="lime">青柠色</option>
                <option value="green">绿色</option>
                <option value="emerald">翡翠色</option>
                <option value="teal">蓝绿色</option>
                <option value="cyan">青色</option>
                <option value="sky">天空蓝</option>
                <option value="blue">蓝色</option>
                <option value="indigo">靛蓝色</option>
                <option value="violet">紫罗兰</option>
                <option value="purple">紫色</option>
                <option value="fuchsia">紫红色</option>
                <option value="pink">粉色</option>
                <option value="rose">玫瑰色</option>
                <option value="white">白色</option>
              </select>
              <p className="form-help">选择主色调，会影响整体配色方案</p>
            </div>

            <div className="form-group">
              <label className="form-label">图标样式</label>
              <select className="select" {...register('iconStyle')}>
                <option value="">默认</option>
                <option value="gradient">渐变</option>
                <option value="theme">主题</option>
              </select>
              <p className="form-help">图标的默认样式，渐变或主题匹配</p>
            </div>

            <div className="form-group">
              <label className="form-label">卡片背景模糊</label>
              <select className="select" {...register('cardBlur')}>
                <option value="">无</option>
                <option value="xs">极小</option>
                <option value="sm">小</option>
                <option value="md">中等</option>
                <option value="lg">大</option>
                <option value="xl">超大</option>
              </select>
              <p className="form-help">为服务和书签卡片应用模糊过滤器</p>
            </div>

            <div className="form-group">
              <label className="form-label">书签样式</label>
              <select className="select" {...register('bookmarksStyle')}>
                <option value="">默认</option>
                <option value="icons">仅图标</option>
              </select>
              <p className="form-help">全局书签显示样式</p>
            </div>

            <div className="form-group">
              <p className="text-sm text-gray-400">
                💡 提示：状态样式设置已移至 <span className="text-blue-400 font-medium">服务配置 → Ping 检测</span> 中，可在启用 Ping 时进行全局设置
              </p>
            </div>
          </div>
        )}
      </SettingsCard>

      {/* 页面设置 */}
      <SettingsCard
        title="页面设置"
        defaultValues={{
          headerStyle: settingsConfig?.headerStyle || 'clean',
          target: settingsConfig?.target || '_blank',
          hideVersion: settingsConfig?.hideVersion || false,
          showStats: settingsConfig?.showStats || false,
          disableUpdateCheck: settingsConfig?.disableUpdateCheck || false,
          hideErrors: settingsConfig?.hideErrors || false
        }}
      >
        {({ register, watchedValues }) => (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="form-group">
              <label className="form-label">头部样式</label>
              <select className="select" {...register('headerStyle')}>
                <option value="clean">简洁</option>
                <option value="boxed">盒装</option>
                <option value="underlined">下划线</option>
                <option value="boxedWidgets">盒装小部件</option>
              </select>
              <p className="form-help">选择页面头部的显示样式</p>
            </div>

            <div className="form-group">
              <label className="form-label">链接打开方式</label>
              <select className="select" {...register('target')}>
                <option value="_blank">新标签页</option>
                <option value="_self">当前页面</option>
                <option value="_top">新窗口</option>
              </select>
              <p className="form-help">设置链接的默认打开方式</p>
            </div>

            <div className="form-group">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="hideVersion"
                  className="mr-2"
                  {...register('hideVersion')}
                />
                <label htmlFor="hideVersion" className="form-label mb-0">
                  隐藏版本信息
                </label>
              </div>
              <p className="form-help">隐藏页面底部的版本显示</p>
            </div>

            <div className="form-group">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="showStats"
                  className="mr-2"
                  {...register('showStats')}
                />
                <label htmlFor="showStats" className="form-label mb-0">
                  显示统计信息
                </label>
              </div>
              <p className="form-help">显示Docker扩展统计信息</p>
            </div>

            <div className="form-group">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="disableUpdateCheck"
                  className="mr-2"
                  {...register('disableUpdateCheck')}
                />
                <label htmlFor="disableUpdateCheck" className="form-label mb-0">
                  禁用更新检查
                </label>
              </div>
              <p className="form-help">禁用从GitHub检查新版本</p>
            </div>

            <div className="form-group">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="hideErrors"
                  className="mr-2"
                  {...register('hideErrors')}
                />
                <label htmlFor="hideErrors" className="form-label mb-0">
                  隐藏错误消息
                </label>
              </div>
              <p className="form-help">全局隐藏API错误消息</p>
            </div>
          </div>
        )}
      </SettingsCard>

      {/* 布局与显示 */}
      <SettingsCard
        title="布局与显示"
        defaultValues={{
          fullWidth: settingsConfig?.fullWidth || false,
          maxGroupColumns: settingsConfig?.maxGroupColumns || 4,
          maxBookmarkGroupColumns: settingsConfig?.maxBookmarkGroupColumns || 6,
          useEqualHeights: settingsConfig?.useEqualHeights || false,
          disableCollapse: settingsConfig?.disableCollapse || false,
          groupsInitiallyCollapsed: settingsConfig?.groupsInitiallyCollapsed || false
        }}
      >
        {({ register, watchedValues }) => (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="form-group">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="fullWidth"
                  className="mr-2"
                  {...register('fullWidth')}
                />
                <label htmlFor="fullWidth" className="form-label mb-0">
                  全宽模式
                </label>
              </div>
              <p className="form-help">让主页占据整个窗口宽度</p>
            </div>

            <div className="form-group">
              <label className="form-label">最大组列数</label>
              <input
                type="number"
                className="input"
                {...register('maxGroupColumns', {
                  valueAsNumber: true,
                  min: 4,
                  max: 8
                })}
                min="4"
                max="8"
                placeholder="4"
              />
              <p className="form-help">大屏幕上服务组的最大列数 (4-8)</p>
            </div>

            <div className="form-group">
              <label className="form-label">书签组最大列数</label>
              <input
                type="number"
                className="input"
                {...register('maxBookmarkGroupColumns', {
                  valueAsNumber: true,
                  min: 5,
                  max: 8
                })}
                min="5"
                max="8"
                placeholder="6"
              />
              <p className="form-help">书签组的最大列数 (5-8)</p>
            </div>

            <div className="form-group">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="useEqualHeights"
                  className="mr-2"
                  {...register('useEqualHeights')}
                />
                <label htmlFor="useEqualHeights" className="form-label mb-0">
                  等高卡片
                </label>
              </div>
              <p className="form-help">一行中的所有卡片保持相同高度</p>
            </div>

            <div className="form-group">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="disableCollapse"
                  className="mr-2"
                  {...register('disableCollapse')}
                />
                <label htmlFor="disableCollapse" className="form-label mb-0">
                  禁用折叠功能
                </label>
              </div>
              <p className="form-help">禁用服务和书签的可折叠功能</p>
            </div>

            <div className="form-group">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="groupsInitiallyCollapsed"
                  className="mr-2"
                  {...register('groupsInitiallyCollapsed')}
                />
                <label htmlFor="groupsInitiallyCollapsed" className="form-label mb-0">
                  初始折叠分组
                </label>
              </div>
              <p className="form-help">页面加载时分组默认处于折叠状态</p>
            </div>
          </div>
        )}
      </SettingsCard>

      {/* 快速启动设置 */}
      <SettingsCard
        title="快速启动设置"
        defaultValues={{
          searchDescriptions: settingsConfig?.quicklaunch?.searchDescriptions || false,
          hideInternetSearch: settingsConfig?.quicklaunch?.hideInternetSearch || false,
          showSearchSuggestions: settingsConfig?.quicklaunch?.showSearchSuggestions || false,
          provider: settingsConfig?.quicklaunch?.provider || 'google',
          hideVisitURL: settingsConfig?.quicklaunch?.hideVisitURL || false,
          mobileButtonPosition: settingsConfig?.quicklaunch?.mobileButtonPosition || '',
          url: settingsConfig?.quicklaunch?.url || '',
          target: settingsConfig?.quicklaunch?.target || '_blank',
          suggestionUrl: settingsConfig?.quicklaunch?.suggestionUrl || ''
        }}
        sectionKey="quicklaunch"
      >
        {({ register, watch }) => {
          const provider = watch('provider')
          return (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="form-group">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="searchDescriptions"
                      className="mr-2"
                      {...register('searchDescriptions')}
                    />
                    <label htmlFor="searchDescriptions" className="form-label mb-0">
                      搜索服务描述
                    </label>
                  </div>
                  <p className="form-help">允许在搜索时包含服务描述内容</p>
                </div>

                <div className="form-group">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="hideInternetSearch"
                      className="mr-2"
                      {...register('hideInternetSearch')}
                    />
                    <label htmlFor="hideInternetSearch" className="form-label mb-0">
                      隐藏网络搜索
                    </label>
                  </div>
                  <p className="form-help">禁用快速启动中的网络搜索</p>
                </div>

                <div className="form-group">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="showSearchSuggestions"
                      className="mr-2"
                      {...register('showSearchSuggestions')}
                    />
                    <label htmlFor="showSearchSuggestions" className="form-label mb-0">
                      显示搜索建议
                    </label>
                  </div>
                  <p className="form-help">显示互联网搜索的搜索建议</p>
                </div>

                <div className="form-group">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="hideVisitURL"
                      className="mr-2"
                      {...register('hideVisitURL')}
                    />
                    <label htmlFor="hideVisitURL" className="form-label mb-0">
                      隐藏访问URL
                    </label>
                  </div>
                  <p className="form-help">禁用检测并提供打开URL的选项</p>
                </div>

                <div className="form-group">
                  <label className="form-label">搜索引擎提供商</label>
                  <select className="select" {...register('provider')}>
                    <option value="google">Google</option>
                    <option value="duckduckgo">DuckDuckGo</option>
                    <option value="bing">Bing</option>
                    <option value="baidu">百度</option>
                    <option value="brave">Brave</option>
                    <option value="custom">自定义</option>
                  </select>
                  <p className="form-help">选择默认搜索引擎</p>
                </div>

                <div className="form-group">
                  <label className="form-label">移动按钮位置</label>
                  <select className="select" {...register('mobileButtonPosition')}>
                    <option value="">禁用</option>
                    <option value="top-left">左上角</option>
                    <option value="top-right">右上角</option>
                    <option value="bottom-left">左下角</option>
                    <option value="bottom-right">右下角</option>
                  </select>
                  <p className="form-help">移动设备上快速启动按钮的位置</p>
                </div>
              </div>

              {provider === 'custom' && (
                <div className="bg-yellow-900/20 border border-yellow-500/30 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-yellow-300 mb-3">自定义搜索引擎设置</h4>
                  <div className="grid grid-cols-1 gap-4">
                    <div className="form-group">
                      <label className="form-label">搜索URL</label>
                      <input
                        type="text"
                        className="input"
                        {...register('url')}
                        placeholder="https://www.ecosia.org/search?q="
                      />
                      <p className="form-help">自定义搜索引擎的URL，查询词会附加在末尾</p>
                    </div>

                    <div className="form-group">
                      <label className="form-label">链接目标</label>
                      <select className="select" {...register('target')}>
                        <option value="_blank">新标签页</option>
                        <option value="_self">当前页面</option>
                        <option value="_top">新窗口</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label className="form-label">搜索建议URL</label>
                      <input
                        type="text"
                        className="input"
                        {...register('suggestionUrl')}
                        placeholder="https://ac.ecosia.org/autocomplete?type=list&q="
                      />
                      <p className="form-help">获取搜索建议的API地址（可选）</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )
        }}
      </SettingsCard>

      {/* 背景设置 */}
      <SettingsCard
        title="背景设置"
        defaultValues={{
          image: settingsConfig?.background?.image || '',
          blur: settingsConfig?.background?.blur || 'sm',
          brightness: settingsConfig?.background?.brightness || 50,
          saturate: settingsConfig?.background?.saturate || 100,
          opacity: settingsConfig?.background?.opacity || 20
        }}
        sectionKey="background"
      >
        {({ register, watchedValues }) => (
          <div className="space-y-4">
            <div className="form-group">
              <label className="form-label">背景图片 URL</label>
              <input
                type="text"
                className="input"
                {...register('image')}
                placeholder="https://example.com/background.jpg"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="form-group">
                <label className="form-label">模糊程度</label>
                <select className="select" {...register('blur')}>
                  <option value="none">无</option>
                  <option value="sm">小</option>
                  <option value="md">中</option>
                  <option value="lg">大</option>
                  <option value="xl">超大</option>
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">亮度</label>
                <select className="select" {...register('brightness')}>
                  <option value="0">0 (最暗)</option>
                  <option value="50">50 (较暗)</option>
                  <option value="75">75 (稍暗)</option>
                  <option value="90">90 (略暗)</option>
                  <option value="95">95 (接近正常)</option>
                  <option value="100">100 (正常)</option>
                  <option value="105">105 (略亮)</option>
                  <option value="110">110 (稍亮)</option>
                  <option value="125">125 (较亮)</option>
                  <option value="150">150 (很亮)</option>
                  <option value="200">200 (最亮)</option>
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">饱和度</label>
                <select className="select" {...register('saturate')}>
                  <option value="0">0 (灰度)</option>
                  <option value="50">50 (低饱和)</option>
                  <option value="100">100 (正常)</option>
                  <option value="125">125 (稍高)</option>
                  <option value="150">150 (较高)</option>
                  <option value="200">200 (高饱和)</option>
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">透明度 (%)</label>
                <input
                  type="number"
                  className="input"
                  {...register('opacity', {
                    valueAsNumber: true,
                    min: 0,
                    max: 100
                  })}
                  min="0"
                  max="100"
                  placeholder="20"
                />
              </div>
            </div>

            <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-blue-300 mb-1">关于分组背景图</h4>
                  <p className="text-sm text-blue-200">
                    Homepage 原生不支持为单个分组设置背景图片。背景图片只能应用于整个页面。
                    如需为特定区域添加背景效果，建议使用自定义 CSS。
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </SettingsCard>

      {/* 高级配置 */}
      <SettingsCard
        title="高级配置"
        defaultValues={{
          logpath: settingsConfig?.logpath || '',
          providers: JSON.stringify(settingsConfig?.providers || {}, null, 2)
        }}
      >
        {({ register, watchedValues }) => (
          <div className="space-y-4">
            <div className="form-group">
              <label className="form-label">日志路径</label>
              <input
                type="text"
                className="input"
                {...register('logpath')}
                placeholder="/logfile/path"
              />
              <p className="form-help">自定义日志文件的写入路径，默认为config/logs</p>
            </div>

            <div className="form-group">
              <label className="form-label">API提供商配置</label>
              <textarea
                className="input h-32 font-mono text-sm"
                {...register('providers')}
                placeholder={`{
  "openweathermap": "your-api-key",
  "finnhub": "your-finnhub-key",
  "longhorn": {
    "url": "https://longhorn.example.com",
    "username": "admin",
    "password": "password"
  }
}`}
              />
              <p className="form-help">小部件API提供商的配置，JSON格式。配置后可在小部件中使用provider而不是apiKey</p>
            </div>

            <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-blue-300 mb-1">关于环境变量</h4>
                  <p className="text-sm text-blue-200 mb-2">
                    以下配置需要通过环境变量设置，无法通过Web界面修改：
                  </p>
                  <ul className="text-sm text-blue-200 space-y-1">
                    <li>• <code className="bg-blue-800/50 px-1 rounded">LOG_TARGETS</code> - 日志输出目标 (both/stdout/file)</li>
                    <li>• <code className="bg-blue-800/50 px-1 rounded">NODE_ENV</code> - 运行环境 (development/production)</li>
                    <li>• <code className="bg-blue-800/50 px-1 rounded">PORT</code> - 服务器端口</li>
                    <li>• <code className="bg-blue-800/50 px-1 rounded">CONFIG_PATH</code> - 配置文件路径</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}
      </SettingsCard>
    </div>
  )
}

export default SettingsConfig
